/**
 * Native system bar (status + navigation) appearance.
 *
 * The style MUST be driven through Capacitor 8's core `SystemBars` plugin: it
 * remembers the last style it was given and re-applies it on every
 * configuration change (rotation, OS dark-mode switch), silently reverting a
 * flag set through any other API.
 *
 * The background and the icon colour have to be decided together, from ONE
 * colour, because where that colour comes from depends on the platform:
 *
 *  - Android below 15 with `overlaysWebView: false`: the status bar is its own
 *    strip and we paint it ourselves, so the web page behind it is irrelevant.
 *    Setting only the icon style is what produced white icons on the white
 *    strip the plugin config had painted.
 *  - Android 15+: edge-to-edge is enforced, `setBackgroundColor` is ignored and
 *    the web page shows through, so the app header IS the background.
 *
 * Feeding both calls the same colour makes the two cases agree.
 *
 * Two layers report that colour, and the one closest to the screen wins: the
 * platform (branding) surface for office screens, and the technician interface
 * preset, which paints its own header while its shell is mounted.
 *
 * The navigation bar follows the PLATFORM colour only. The bottom of the app is
 * the tab bar, not the header, so styling it from a dark preset header turns
 * the gesture pill white on a white tab bar.
 *
 * The @capacitor/status-bar plugin is told the same style even though SystemBars
 * already applied it: that plugin re-applies ITS remembered style on every
 * configuration change, and its handler runs last. Left unsynced, it restores
 * the `style` from capacitor.config.ts, so a rotation or an OS dark-mode switch
 * would undo everything above.
 */
import { SystemBars, SystemBarsStyle, SystemBarType } from '@capacitor/core'
import { isNative } from './platform'
import { isDarkSurface } from '@/utils/color'

const DEFAULT_COLOR = '#FFFFFF'

let platformColor = DEFAULT_COLOR
// null = no interface preset is on screen, so the platform layer decides.
let interfaceColor = null
let applied = null

// Capacitor's naming describes the BACKGROUND, not the icons:
// Dark = light icons for a dark bar, Light = dark icons for a light bar.
function styleFor(color) {
  return isDarkSurface(color) ? SystemBarsStyle.Dark : SystemBarsStyle.Light
}

async function apply() {
  if (!isNative()) return
  const statusColor = interfaceColor || platformColor
  const key = `${statusColor}|${platformColor}`
  if (key === applied) return
  applied = key

  try {
    await SystemBars.setStyle({ style: styleFor(statusColor), bar: SystemBarType.StatusBar })
    await SystemBars.setStyle({ style: styleFor(platformColor), bar: SystemBarType.NavigationBar })

    const { StatusBar, Style } = await import('@capacitor/status-bar')
    // Silently ignored on Android 15+, which is exactly where the page already
    // paints this area itself; on iOS it colours the strip above the WebView.
    await StatusBar.setBackgroundColor({ color: statusColor })
    await StatusBar.setStyle({ style: isDarkSurface(statusColor) ? Style.Dark : Style.Light })
  } catch (e) {
    applied = null
    console.warn('[SystemBars] apply failed:', e?.message || e)
  }
}

/** Platform (branding) layer: the app surface colour behind both bars. */
export function setPlatformBarsColor(color) {
  platformColor = color || DEFAULT_COLOR
  return apply()
}

/** Interface preset layer: its header colour, or `null` to release the bar. */
export function setInterfaceBarsColor(color) {
  interfaceColor = color || null
  return apply()
}
